import { useState, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { alertToast } from "@/contexts/AlertToastContext";
import {
  Loader2,
  Phone,
  Package,
  ArrowRight,
  MapPin,
  Clock,
  Mail,
  PhoneCall,
  ArrowLeft,
  Shield,
  CheckCircle2,
} from "lucide-react";
import Header from "@/components/Header";
import { Footer2 } from "@/components/ui/shadcnblocks-com-footer2";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

interface RepairOrder {
  id: string;
  order_number: number;
  title: string;
  description: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  guest_phone: string | null;
}

const STATUS_OPTIONS = [
  { value: "pending", label: "ממתין", color: "bg-amber-500/10 text-amber-600 border-amber-500/20" },
  { value: "in_repair", label: "בתיקון", color: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
  { value: "completed", label: "הושלם", color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20" },
  { value: "waiting_in_store", label: "מחכה בחנות", color: "bg-orange-500/10 text-orange-600 border-orange-500/20" },
  { value: "shipped", label: "נשלח", color: "bg-violet-500/10 text-violet-600 border-violet-500/20" },
  { value: "delivered", label: "נמסר", color: "bg-green-500/10 text-green-600 border-green-500/20" },
  { value: "cancelled", label: "בוטל", color: "bg-red-500/10 text-red-600 border-red-500/20" },
];

const TrackOrder = () => {
  const [phone, setPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [step, setStep] = useState<"phone" | "otp" | "orders">("phone");
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [repairs, setRepairs] = useState<RepairOrder[]>([]);
  const [hasExistingAccount, setHasExistingAccount] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === "otp") {
      setTimeout(() => {
        otpInputRef.current?.focus();
      }, 100);
    }
  }, [step]);

  // Cooldown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, "");
    return digits;
  };

  const handleSendOtp = async () => {
    if (!phone || phone.length < 9) {
      alertToast.error("נא להזין מספר טלפון תקין");
      return;
    }

    setIsSendingOtp(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-otp", {
        body: { phone },
      });

      if (error) {
        console.error("Error sending OTP:", error);
        alertToast.error("שגיאה בשליחת קוד האימות");
        return;
      }

      if (data?.error) {
        alertToast.error(data.error);
        return;
      }

      alertToast.success("קוד אימות נשלח לטלפון שלך");
      setStep("otp");
      setResendCooldown(60);
    } catch (err) {
      console.error("Failed to send OTP:", err);
      alertToast.error("שגיאה בשליחת קוד האימות");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async (code: string) => {
    if (!code || code.length !== 6) {
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("verify-otp", {
        body: { phone, code },
      });

      if (error) {
        console.error("Error verifying OTP:", error);
        alertToast.error("שגיאה באימות הקוד");
        setOtpCode("");
        return;
      }

      if (data?.error) {
        alertToast.error(data.error);
        setOtpCode("");
        return;
      }

      alertToast.success("אימות בוצע בהצלחה");
      setRepairs(data?.repairs || []);
      setHasExistingAccount(data?.hasExistingAccount || false);
      setStep("orders");
    } catch (err) {
      console.error("Failed to verify OTP:", err);
      alertToast.error("שגיאה באימות הקוד");
      setOtpCode("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (value: string) => {
    setOtpCode(value);
    if (value.length === 6) {
      handleVerifyOtp(value);
    }
  };

  const handleResendOtp = async () => {
    setOtpCode("");
    await handleSendOtp();
  };

  const getStatusBadge = (statusValue: string) => {
    const statusOption = STATUS_OPTIONS.find((s) => s.value === statusValue);
    return (
      <Badge
        variant="outline"
        className={`${statusOption?.color || "bg-muted text-muted-foreground"} border font-medium`}
      >
        {statusOption?.label || statusValue}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleStartOver = () => {
    setPhone("");
    setOtpCode("");
    setRepairs([]);
    setHasExistingAccount(false);
    setStep("phone");
  };

  const getStatusStep = (status: string) => {
    const steps = ["pending", "in_repair", "waiting_in_store", "delivered"];
    return steps.indexOf(status) + 1;
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" dir="rtl">
      <Header />
      <main className="flex-1 mt-24 md:mt-28">
        {/* Hero Section */}
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-2xl mx-auto"
          >
            <h1 className="text-3xl md:text-4xl font-bold mb-4">מעקב אחרי הזמנה</h1>
            <p className="text-muted-foreground text-lg">עקבו אחרי סטטוס ההזמנה שלכם בזמן אמת</p>
          </motion.div>
        </div>

        <div className="container mx-auto px-4 py-8 md:py-12">
          <div className="max-w-4xl mx-auto">
            {step === "phone" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Card className="border border-border/50 shadow-none bg-card overflow-hidden">
                  <CardContent className="p-0">
                    <div className="grid md:grid-cols-2">
                      {/* Form Section */}
                      <div className="p-6 md:p-10 space-y-6">
                        <div className="flex items-center gap-3">
                          <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                            <Phone className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <h2 className="text-xl font-bold">הזינו את מספר הטלפון</h2>
                            <p className="text-sm text-muted-foreground">נשלח אליכם קוד אימות</p>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div className="space-y-2">
                            <label className="text-sm font-medium">מספר טלפון</label>
                            <Input
                              type="tel"
                              placeholder="05X-XXXXXXX"
                              value={phone}
                              onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                              className="text-left h-12 text-lg"
                              dir="ltr"
                            />
                          </div>

                          <Button
                            onClick={handleSendOtp}
                            className="w-full h-12 text-base"
                            disabled={isSendingOtp || phone.length < 9}
                          >
                            {isSendingOtp ? (
                              <>
                                <Loader2 className="ml-2 h-5 w-5 animate-spin" />
                                שולח קוד...
                              </>
                            ) : (
                              <>
                                שלחו לי קוד אימות
                                <ArrowLeft className="mr-2 h-5 w-5" />
                              </>
                            )}
                          </Button>
                        </div>

                        <div className="pt-4 border-t">
                          <p className="text-sm text-muted-foreground text-center">
                            יש לך חשבון?{" "}
                            <Link to="/profile?tab=repairs" className="text-primary font-medium hover:underline">
                              התחברו כאן
                            </Link>
                          </p>
                        </div>
                      </div>

                      {/* Info Section */}
                      <div className="bg-muted/50 p-6 md:p-10 space-y-6 hidden md:block">
                        <h3 className="font-semibold text-lg">איך זה עובד?</h3>
                        <div className="space-y-4">
                          <div className="flex gap-3">
                            <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0">
                              1
                            </div>
                            <div>
                              <p className="font-medium">הזינו מספר טלפון</p>
                              <p className="text-sm text-muted-foreground">
                                מספר הטלפון מופיע בקבלה שקיבלתם בעת מסירת התיקון
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-3">
                            <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0">
                              2
                            </div>
                            <div>
                              <p className="font-medium">אמתו את הזהות</p>
                              <p className="text-sm text-muted-foreground">הזינו את קוד האימות שנשלח</p>
                            </div>
                          </div>
                          <div className="flex gap-3">
                            <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0">
                              3
                            </div>
                            <div>
                              <p className="font-medium">צפו בהזמנות</p>
                              <p className="text-sm text-muted-foreground">עקבו אחרי הסטטוס בזמן אמת</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {step === "otp" && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <Card className="border border-border/50 shadow-none bg-card max-w-md mx-auto">
                  <CardContent className="p-6 md:p-10 space-y-6">
                    <div className="text-center space-y-2">
                      <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                        <Shield className="h-8 w-8 text-primary" />
                      </div>
                      <h2 className="text-2xl font-bold">הזינו את קוד האימות</h2>
                      <p className="text-muted-foreground">
                        קוד אימות נשלח למספר{" "}
                        <span className="font-medium text-foreground" dir="ltr">
                          {phone}
                        </span>
                      </p>
                    </div>

                    <div className="flex justify-center py-4" dir="ltr">
                      <InputOTP
                        ref={otpInputRef}
                        maxLength={6}
                        value={otpCode}
                        onChange={handleOtpChange}
                        disabled={isLoading}
                      >
                        <InputOTPGroup className="gap-2 md:gap-3">
                          <InputOTPSlot index={0} className="h-12 w-10 md:h-14 md:w-12 text-xl" />
                          <InputOTPSlot index={1} className="h-12 w-10 md:h-14 md:w-12 text-xl" />
                          <InputOTPSlot index={2} className="h-12 w-10 md:h-14 md:w-12 text-xl" />
                          <InputOTPSlot index={3} className="h-12 w-10 md:h-14 md:w-12 text-xl" />
                          <InputOTPSlot index={4} className="h-12 w-10 md:h-14 md:w-12 text-xl" />
                          <InputOTPSlot index={5} className="h-12 w-10 md:h-14 md:w-12 text-xl" />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>

                    {isLoading && (
                      <div className="flex items-center justify-center gap-2 text-primary">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>מאמת...</span>
                      </div>
                    )}

                    <div className="flex justify-between text-sm pt-2">
                      <button
                        onClick={handleResendOtp}
                        className={`font-medium ${resendCooldown > 0 ? "text-muted-foreground cursor-not-allowed" : "text-primary hover:underline"}`}
                        disabled={isSendingOtp || resendCooldown > 0}
                      >
                        {isSendingOtp
                          ? "שולח..."
                          : resendCooldown > 0
                            ? `שלח קוד חדש (${resendCooldown})`
                            : "שלח קוד חדש"}
                      </button>
                      <button
                        onClick={handleStartOver}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        שנה מספר טלפון
                      </button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {step === "orders" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="space-y-8"
              >
                {/* Account Signup Suggestion - only show if no existing account */}
                {!hasExistingAccount && (
                  <Card className="border border-primary/20 bg-primary/5 shadow-none">
                    <CardContent className="p-4 md:p-6">
                      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                        <div className="flex items-start gap-3">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <CheckCircle2 className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-foreground">פתחו חשבון וקבלו הטבות!</h3>
                            <p className="text-sm text-muted-foreground">
                              הצטרפו אלינו עם מספר הטלפון {phone} וקבלו עדכונים, הנחות בלעדיות ומעקב נוח יותר
                            </p>
                          </div>
                        </div>
                        <Button asChild className="shrink-0">
                          <Link to="/profile?tab=repairs">
                            לאזור האישי
                            <ArrowLeft className="mr-2 h-4 w-4" />
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div>
                    <h2 className="text-2xl font-bold">ההזמנות שלך</h2>
                    <p className="text-muted-foreground">{repairs.length} הזמנות נמצאו</p>
                  </div>
                  <Button variant="outline" onClick={handleStartOver}>
                    <ArrowRight className="ml-2 h-4 w-4" />
                    בדוק הזמנה אחרת
                  </Button>
                </div>

                {repairs.length === 0 ? (
                  <Card className="border border-border/50 shadow-none">
                    <CardContent className="py-16 text-center">
                      <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
                        <Package className="h-10 w-10 text-muted-foreground" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2">לא נמצאו הזמנות</h3>
                      <p className="text-muted-foreground max-w-sm mx-auto">
                        לא נמצאו הזמנות למספר זה. אם סגרתם הזמנה לאחרונה, ייתכן שהיא עדיין לא הוזנה למערכת.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-6">
                    {repairs.map((repair, index) => (
                      <motion.div
                        key={repair.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                      >
                        <Card className="border border-border/50 shadow-none overflow-hidden">
                          <CardContent className="p-0">
                            {/* Status Progress */}
                            {repair.status !== "cancelled" && (
                              <div className="bg-muted/50 p-4 border-b">
                                <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                                  <span>ממתין</span>
                                  <span>בתיקון</span>
                                  <span>מחכה בחנות</span>
                                  <span>נמסר</span>
                                </div>
                                <div className="h-2 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-primary rounded-full transition-all duration-500"
                                    style={{ width: `${(getStatusStep(repair.status) / 4) * 100}%` }}
                                  />
                                </div>
                              </div>
                            )}

                            <div className="p-6 space-y-4">
                              <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-muted-foreground">פנייה מספר</span>
                                    <span className="font-bold text-2xl">#{repair.order_number}</span>
                                  </div>
                                  <h3 className="text-lg font-semibold">{repair.title}</h3>
                                </div>
                                {getStatusBadge(repair.status)}
                              </div>

                              {repair.description && (
                                <p className="text-muted-foreground bg-muted/50 p-3 rounded-lg">{repair.description}</p>
                              )}

                              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground pt-4 border-t">
                                <div className="flex items-center gap-1">
                                  <Clock className="h-4 w-4" />
                                  <span>נוצר: {formatDate(repair.created_at)}</span>
                                </div>
                                {repair.updated_at !== repair.created_at && (
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-4 w-4" />
                                    <span>עודכן: {formatDate(repair.updated_at)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* Store Location & Contact */}
                <Card className="border border-border/50 shadow-none overflow-hidden">
                  <CardContent className="p-0">
                    <div className="grid md:grid-cols-2">
                      {/* Map */}
                      <div className="h-64 md:h-auto md:min-h-[300px]">
                        <iframe
                          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3411.261797287465!2d34.79208012489179!3d31.241175260883782!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x150266113eb89b75%3A0xa6850d035d15c6e8!2z15DXnNeZ16EgfCDXqteb16nXmdeY15nXnSDXldep16LXldeg15nXnQ!5e0!3m2!1siw!2sil!4v1767350674850!5m2!1siw!2sil"
                          width="100%"
                          height="100%"
                          style={{ border: 0, minHeight: "250px" }}
                          allowFullScreen
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          title="מיקום החנות"
                        />
                      </div>

                      {/* Contact Info */}
                      <div className="p-6 md:p-8 space-y-6">
                        <div>
                          <h3 className="text-xl font-bold mb-2">איסוף מהחנות</h3>
                          <p className="text-muted-foreground">ניתן לאסוף את ההזמנה מהחנות שלנו בשעות הפעילות</p>
                        </div>

                        <div className="space-y-4">
                          <div className="flex items-start gap-3">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <MapPin className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">כתובת</p>
                              <p className="text-muted-foreground">הרצל 58, באר שבע</p>
                            </div>
                          </div>

                          <div className="flex items-start gap-3">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <PhoneCall className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">טלפון</p>
                              <a
                                href="tel:0795575576"
                                className="text-muted-foreground hover:text-primary transition-colors"
                                dir="ltr"
                              >
                                0795575576
                              </a>
                            </div>
                          </div>

                          <div className="flex items-start gap-3">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <Clock className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">שעות פעילות</p>
                              <div className="text-muted-foreground text-sm space-y-1">
                                <p>ראשון: 10:00-19:00</p>
                                <p>שני: 10:00-19:00</p>
                                <p>שלישי: 09:45-14:00</p>
                                <p>רביעי: 10:00-19:00</p>
                                <p>חמישי: 10:00-19:00</p>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-start gap-3">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              <Mail className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium">אימייל</p>
                              <a
                                href="mailto:info@alice-jewelry.co.il"
                                className="text-muted-foreground hover:text-primary transition-colors"
                              >
                                info@alice-jewelry.co.il
                              </a>
                            </div>
                          </div>
                        </div>

                        <Button variant="outline" className="w-full" asChild>
                          <a
                            href="https://waze.com/ul?ll=31.241175,34.794269&navigate=yes"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <MapPin className="ml-2 h-4 w-4" />
                            נווט עם Waze
                          </a>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      </main>
      <Footer2 />
    </div>
  );
};

export default TrackOrder;
